import { useState, useEffect } from 'react';
import { X, Clock } from "lucide-react";
import { EcGift as Gift } from "@/components/icons/EcGift";
import { EcMark } from "@/components/icons/EcMark";
import { Button } from '@/components/ui/button';
import { useReferralTracking, REFERRAL_CONFIG } from '@/hooks/useReferralTracking';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import CountdownTimer from '@/components/CountdownTimer';

export function ReferralDiscountBanner() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { referralDiscount, hasActiveDiscount, clearDiscount } = useReferralTracking();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if banner was dismissed in this session
    const dismissed = sessionStorage.getItem('referral_banner_dismissed');
    if (dismissed) {
      setIsDismissed(true);
    }
    
    // Small delay for animation
    if (hasActiveDiscount && !dismissed) {
      setTimeout(() => setIsVisible(true), 500);
    }
  }, [hasActiveDiscount]);

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem('referral_banner_dismissed', 'true');
  };

  const handleExpire = () => {
    clearDiscount();
    setIsVisible(false);
  };

  const handleViewPlans = () => {
    navigate('/pricing');
  };

  if (!hasActiveDiscount || isDismissed || !referralDiscount) {
    return null;
  }

  return (
    <div 
      className={`fixed bottom-4 right-4 z-50 max-w-md transition-all duration-500 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
      }`}
    >
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary via-primary/90 to-accent shadow-2xl border border-primary/20">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-foreground/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-foreground/5 rounded-full blur-2xl" />
        
        {/* Dismiss button */}
        <button 
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/20 transition-colors text-white/70 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="relative p-4 text-white">
          {/* Header */}
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-lg bg-white/20">
              <Gift className="h-4 w-4" />
            </div>
            <span className="text-sm font-medium opacity-90">
              {language === 'el' ? 'Ειδική Προσφορά Referral' : 'Special Referral Offer'}
            </span>
          </div>

          {/* Discount amount */}
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-4xl font-bold">{REFERRAL_CONFIG.DISCOUNT_PERCENT}%</span>
            <span className="text-lg opacity-90">
              {language === 'el' ? 'ΕΚΠΤΩΣΗ' : 'OFF'}
            </span>
            <EcMark className="h-5 w-5 animate-pulse opacity-80" />
          </div>

          {/* Description */}
          <p className="text-sm opacity-80 mb-4 leading-relaxed">
            {language === 'el' 
              ? 'Σε όλα τα πλάνα! Για τον πρώτο μήνα (subscription) ή την πρώτη αγορά (Pay-As-You-Go).'
              : 'On all plans! For the first month (subscription) or first purchase (Pay-As-You-Go).'}
          </p>

          {/* Countdown */}
          <div className="bg-foreground/10 backdrop-blur-sm rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-primary-foreground" />
              <span className="text-xs font-medium opacity-90">
                {language === 'el' ? 'Η προσφορά λήγει σε:' : 'Offer expires in:'}
              </span>
            </div>
            <CountdownTimer 
              targetDate={referralDiscount.expiresAt} 
              onExpire={handleExpire}
              size="sm"
              showLabels={true}
            />
          </div>

          {/* CTA Button */}
          <Button 
            onClick={handleViewPlans}
            className="w-full bg-white text-primary hover:bg-white/90 font-semibold"
          >
            {language === 'el' ? 'View Plans' : 'View Plans'}
          </Button>

          {/* Fine print */}
          <p className="text-[10px] opacity-60 mt-2 text-center">
            {language === 'el' 
              ? 'Ισχύει μόνο για νέους χρήστες χωρίς προηγούμενη αγορά'
              : 'Valid only for new users with no prior purchases'}
          </p>
        </div>
      </div>
    </div>
  );
}
